import {
  useEffect,
  useMemo,
  useState,
  useRef,
  FC,
  MouseEvent,
  useCallback,
} from 'react'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Collapse from '@mui/material/Collapse'
import { Box, Divider, IconButton, ListItem, Typography } from '@mui/material'
import {
  ListElementIcon,
  ListShowMoreIcon,
  VisibilityIcon,
} from '../../../Svgs'
import {
  BuildingElementType,
  BuildingElementSubType,
} from '../../../../models/Construction'
import { SelectionManager } from '../../../../visualization/selection/selectionManager'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../store'
import {
  setSelectedElementsIds,
  updateHiddenStoreyElements,
} from '../../../../actions/construction'
import { validate as isValidUUID } from 'uuid'
import {
  EventType,
  StoreyElementsVisibility,
} from '../../../../utils/EventEmitter'
import { SceneManager } from '../../../../visualization/sceneManager'
import { createSelector } from '@reduxjs/toolkit'
import {
  ConstructionPage,
  ConstructionTab,
} from '../../../../reducers/construction'
import { formatOrdinals } from '../../../../utils/Common'
import { isSelectionIgnored } from '../../../../visualization/utils'

export interface ListElement {
  id: string
  name: string
  subItems: ListElement[]
  type: BuildingElementType
  subType?: BuildingElementSubType
}

interface BuildingElementsListProps {
  isDisabled?: boolean
}

interface NestedListItemProps {
  item: ListElement
  level: number
  selectedElementsIds: string[]
  onSelect: (item: ListElement) => void
  onChangeVisibility: (item: ListElement) => void
  isOpen: boolean
  scrollToElement: (element: HTMLElement | null) => void
  hiddenItems: StoreyElementsVisibility
  disabled?: boolean
}

const NestedListItem: FC<NestedListItemProps> = ({
  item,
  level,
  selectedElementsIds,
  onSelect,
  onChangeVisibility,
  isOpen,
  scrollToElement,
  hiddenItems,
  disabled,
}) => {
  const [open, setOpen] = useState(isOpen)
  const listItemRef = useRef<HTMLDivElement>(null)

  const handleExpandClick = (e: MouseEvent) => {
    if (!disabled) {
      e.stopPropagation()
      setOpen(!open)
    }
  }

  const handleClick = useCallback(() => {
    onSelect(item)
  }, [onSelect, item])

  const isSelected = useMemo(() => {
    return selectedElementsIds.includes(item.id)
  }, [selectedElementsIds, item.id])

  const isHidden = Boolean(hiddenItems[item.id])

  useEffect(() => {
    if (isSelected && listItemRef.current && isOpen) {
      setTimeout(() => {
        scrollToElement(listItemRef.current)
      }, 300)
    }
  }, [isSelected, open, isOpen, scrollToElement])

  useEffect(() => {
    if (isOpen) {
      setOpen(isOpen)
    }
  }, [isOpen])

  const secondaryAction = useMemo(() => {
    if (!isSelected) {
      return null
    }

    return (
      <Box
        gap={'6px'}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            background: '#04002E',
            borderRadius: '4px',
            p: '4px',
          }}
        >
          <Typography
            sx={{
              color: '#FBFBFB',
              fontSize: '9px',
              fontWeight: 500,
              textTransform: 'uppercase',
            }}
          >
            FL: 1
          </Typography>
        </Box>
        <Divider orientation='vertical' flexItem />
        <IconButton
          disabled={disabled}
          disableRipple
          onClick={(e) => {
            e.stopPropagation()
            onChangeVisibility(item)
          }}
        >
          <VisibilityIcon
            sx={{
              fontSize: '12px',
              color: isHidden ? '#FF0000' : '#04002E',
            }}
          />
        </IconButton>
      </Box>
    )
  }, [isSelected, isHidden, disabled, item, onChangeVisibility])

  return (
    <>
      <ListItem
        disablePadding
        secondaryAction={secondaryAction}
        sx={{
          '& .MuiListItemSecondaryAction-root': {
            right: '10px',
          },
        }}
      >
        <ListItemButton
          disabled={disabled}
          onClick={handleClick}
          ref={listItemRef}
          sx={{
            pl: `${10 * level}px`,
            pr: '10px',
            py: '4px',
            borderRadius: '4px',
            backgroundColor: isSelected ? '#E5F0FF' : 'white',
            '&:hover': {
              backgroundColor: isSelected ? '#E5F0FF' : '#F2F8FF',
            },
          }}
        >
          <Box
            onClick={handleExpandClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              transition: 'transform 0.3s',
              transform:
                open && item.subItems.length > 0
                  ? 'rotate(90deg)'
                  : 'rotate(0deg)',
              padding: '7px',
            }}
          >
            {item.subItems.length > 0 ? (
              <ListShowMoreIcon
                sx={{
                  color: '#1705E5',
                  fontSize: '10px',
                }}
              />
            ) : (
              <ListElementIcon
                sx={{
                  color: '#1705E5',
                  fontSize: '10px',
                }}
              />
            )}
          </Box>
          <ListItemText
            primary={item.name.toLowerCase()}
            primaryTypographyProps={{
              sx: {
                color: '#04002E',
                fontSize: '12px',
                textTransform: 'capitalize',
              },
            }}
          />
        </ListItemButton>
      </ListItem>
      {item.subItems.length > 0 && (
        <Collapse in={open} timeout='auto' unmountOnExit>
          <List component='div' disablePadding>
            {item.subItems.map((subItem) => (
              <NestedListItem
                key={subItem.id}
                item={subItem}
                level={level + 1}
                disabled={disabled}
                selectedElementsIds={selectedElementsIds}
                onSelect={onSelect}
                onChangeVisibility={onChangeVisibility}
                isOpen={isOpen}
                scrollToElement={scrollToElement}
                hiddenItems={hiddenItems}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  )
}

type StoreyElements = {
  wallElements: ListElement[]
  doorElements: ListElement[]
  windowElements: ListElement[]
}

const selectBuildingElements = createSelector(
  [
    (state: RootState) => state.construction.selectedConstructionTab,
    (state: RootState) => state.construction.selectedConstructionPage,
    (state: RootState) => state.construction.convertData.building.storeys,
    (state: RootState) => state.construction.convertData.building.roofs,
    (state: RootState) => state.construction.selectedStoreyId,
  ],
  (selectedTab, selectedPage, storeys, roofs, selectedStoreyId) => {
    if (
      selectedTab !== ConstructionTab.Editor ||
      selectedPage !== ConstructionPage.Architecture
    ) {
      return []
    }

    const roofElements = roofs.reduce<ListElement[]>(
      (roofElements, roof, roofIndex) => {
        if (selectedStoreyId && roof.storey_id !== selectedStoreyId) {
          return roofElements
        }

        roofElements.push({
          name: `${formatOrdinals(roofIndex + 1)} Floor Roof`,
          subItems: [],
          type: BuildingElementType.Roof,
          id: roof.id,
        })

        return roofElements
      },
      [],
    )

    const { wallElements, doorElements, windowElements } =
      storeys.reduce<StoreyElements>(
        (storeyElements, storey, storeyIndex) => {
          if (selectedStoreyId && storey.id !== selectedStoreyId) {
            return storeyElements
          }

          storey.walls.forEach((wall, wallIndex) => {
            const wallName = `Wall ${storeyIndex + 1}.${wallIndex + 1}`
            const wallElement: ListElement = {
              id: wall.id,
              name: wallName,
              subItems: [],
              type: BuildingElementType.Wall,
              subType: wall.type,
            }

            const typedWallElements = storeyElements.wallElements.find(
              (item) => item.subType === wall.type,
            )

            if (typedWallElements) {
              typedWallElements.subItems.push(wallElement)
            } else {
              storeyElements.wallElements.push({
                id: `${wall.type} Walls`,
                name: `${wall.type} Walls`,
                subItems: [wallElement],
                type: BuildingElementType.Wall,
                subType: wall.type,
              })
            }

            wall.doors.forEach((door, doorIndex) => {
              const doorName = `Door ${storeyIndex + 1}.${wallIndex + 1}.${doorIndex + 1}`
              const doorElement: ListElement = {
                id: door.id,
                name: doorName,
                subItems: [],
                type: BuildingElementType.Door,
                subType: wall.type,
              }

              const typedDoorElements = storeyElements.doorElements.find(
                (item) => item.subType === wall.type,
              )

              if (typedDoorElements) {
                typedDoorElements.subItems.push(doorElement)
              } else {
                storeyElements.doorElements.push({
                  id: `${wall.type} Doors`,
                  name: `${wall.type} Doors`,
                  subItems: [doorElement],
                  type: BuildingElementType.Door,
                  subType: wall.type,
                })
              }
            })

            wall.windows.forEach((window, windowIndex) => {
              const windowName = `Window ${storeyIndex + 1}.${wallIndex + 1}.${windowIndex + 1}`
              const windowElement: ListElement = {
                id: window.id,
                name: windowName,
                subItems: [],
                type: BuildingElementType.Window,
                subType: wall.type,
              }

              const typedWindowElements = storeyElements.windowElements.find(
                (item) => item.subType === wall.type,
              )

              if (typedWindowElements) {
                typedWindowElements.subItems.push(windowElement)
              } else {
                storeyElements.windowElements.push({
                  id: `${wall.type} Windows`,
                  name: `${wall.type} Windows`,
                  subItems: [windowElement],
                  type: BuildingElementType.Window,
                  subType: wall.type,
                })
              }
            })
          })

          return storeyElements
        },
        {
          wallElements: [],
          doorElements: [],
          windowElements: [],
        },
      )

    const allElements: ListElement[] = [
      {
        id: 'Roofs',
        name: 'Roofs',
        subItems: roofElements,
        type: BuildingElementType.Roof,
      },
      {
        id: 'Walls',
        name: 'Walls',
        subItems: wallElements,
        type: BuildingElementType.Wall,
      },
      {
        id: 'Doors',
        name: 'Doors',
        subItems: doorElements,
        type: BuildingElementType.Door,
      },
      {
        id: 'Windows',
        name: 'Windows',
        subItems: windowElements,
        type: BuildingElementType.Window,
      },
    ].filter((element) => element.subItems.length > 0)

    return allElements
  },
)

const findElementPath = (items: ListElement[], id: string): ListElement[] => {
  for (const item of items) {
    if (item.id === id) {
      return [item]
    }

    if (item.subItems.length > 0) {
      const path = findElementPath(item.subItems, id)
      if (path.length > 0) {
        return [item, ...path]
      }
    }
  }

  return []
}

const selectSelectedElementsIds = createSelector(
  [
    (state: RootState) => state.construction.selectedTool,
    (state: RootState) => state.construction.selectedElementsIds,
  ],
  (selectedTool, selectedElementsIds) => {
    if (isSelectionIgnored(selectedTool)) {
      return []
    } else {
      return selectedElementsIds
    }
  },
)

const ElementsList: FC<BuildingElementsListProps> = ({ isDisabled }) => {
  const dispatch = useDispatch()
  const buildingElements = useSelector(selectBuildingElements)
  const selectedElementsIds = useSelector(selectSelectedElementsIds)
  const hiddenStoreyElements = useSelector(
    (state: RootState) => state.construction.hiddenStoreyElements,
  )

  const handleSelect = useCallback(
    (item: ListElement) => {
      const elementsIds: string[] = []
      const queue: ListElement[] = [item]
      while (queue.length > 0) {
        const item = queue.shift()
        if (!item) {
          break
        }

        if (item.subItems.length > 0) {
          queue.push(...item.subItems)
        } else {
          elementsIds.push(item.id)
        }
      }

      const selectionManager = SelectionManager.getInstance()
      const isSelected = selectedElementsIds.includes(item.id)
      if (isSelected) {
        selectionManager.deselect({ elementsIds, silent: true })
        dispatch(setSelectedElementsIds([]))
      } else {
        selectionManager.select({ elementsIds, silent: true })
        dispatch(setSelectedElementsIds([item.id]))
      }
    },
    [selectedElementsIds, dispatch],
  )

  const handleChangeVisibility = useCallback(
    (item: ListElement) => {
      const isHidden = !hiddenStoreyElements[item.id]
      const newState = { [item.id]: isHidden }
      // Update visibility for parent items if isHidden is false
      if (isHidden === false) {
        const elementPath = findElementPath(buildingElements, item.id)
        elementPath.forEach((item) => {
          newState[item.id] = isHidden
        })
      }

      // Update visibility for sub items
      const queue: ListElement[] = [...item.subItems]
      while (queue.length > 0) {
        const item = queue.shift()
        if (!item) {
          break
        }

        if (item.subItems.length > 0) {
          queue.push(...item.subItems)
        }

        newState[item.id] = isHidden
      }

      dispatch(updateHiddenStoreyElements(newState))

      const hiddenElements = Object.entries(
        newState,
      ).reduce<StoreyElementsVisibility>((obj, [itemId, newValue]) => {
        if (isValidUUID(itemId) && newValue !== hiddenStoreyElements[item.id]) {
          obj[itemId] = newValue
        }

        return obj
      }, {})

      SceneManager.getInstance()
        .getEventEmitter()
        ?.emit(EventType.StoreyElementVisibilityChanged, hiddenElements)
    },
    [hiddenStoreyElements, buildingElements, dispatch],
  )

  const scrollToElement = (element: HTMLElement | null) => {
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }

  const isParentOfSelected = (
    item: ListElement,
    selectedId: string,
  ): boolean => {
    if (item.id === selectedId) {
      return true
    }

    return item.subItems.some((subItem) =>
      isParentOfSelected(subItem, selectedId),
    )
  }

  return (
    <List sx={{ width: '100%' }} component='nav'>
      {buildingElements.map((buildingElement) => (
        <NestedListItem
          key={buildingElement.id}
          item={buildingElement}
          level={1}
          disabled={isDisabled}
          selectedElementsIds={selectedElementsIds}
          onSelect={handleSelect}
          onChangeVisibility={handleChangeVisibility}
          isOpen={isParentOfSelected(buildingElement, selectedElementsIds[0])}
          scrollToElement={scrollToElement}
          hiddenItems={hiddenStoreyElements}
        />
      ))}
    </List>
  )
}

export default ElementsList
